import { useSetAtom } from "jotai";
import { FunctionComponent, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Can } from "#modules/company/view/Can";
import { groupSearchQuoteRequestLinePerBatch } from "#modules/quoteRequestLines/domain/groupSearchQuoteRequestLinePerBatch";
import {
  SearchQuoteRequestLineDto,
  SortData,
} from "#modules/quoteRequestLines/fetchQuoteRequestLines.api";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { NoQRLDisplay } from "#modules/quoteRequestLines/NoQRLDisplay";
import { PredictDropzone } from "#modules/quoteRequestLines/PredictDropzone";
import { QuoteRequestLinesList } from "#modules/quoteRequestLines/QuoteRequestLinesList";
import { QuoteRequestLinesSearchBar } from "#modules/quoteRequestLines/QuoteRequestLinesSearchBar";
import { SearchSimilarPart } from "#modules/quoteRequestLines/SearchSimilarPart";
import { useQuoteRequestLines } from "#modules/quoteRequestLines/useQuoteRequestLines";
import { useUrlState } from "#shared/helpers/useUrlState";
import { PageLayout } from "#shared/layouts/PageLayout";
import { ROUTES } from "#shared/routes";
import { quoteRequestisEditing } from "#shared/view/components/AppShell/Atoms";
import { Group } from "#shared/view/components/Group";
import { Stack } from "#shared/view/components/Stack";

export type QuoteResquestLineWithBatchInfo = QuoteRequestLineDto & {
  isBatch: boolean;
  quoteRequest: SearchQuoteRequestLineDto["quoteRequest"];
};

export const QuoteRequestLinesPage: FunctionComponent = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useUrlState<string>("search", { initialValue: "" });
  const [sort, setSort] = useUrlState<SortData>("sort", { initialValue: "" });

  const { quoteRequestLines, quoteRequestLinesCount, quoteRequestLinesCountPerPage, isLoading } =
    useQuoteRequestLines(search, sort);

  const quoteRequestLinesWithBatchInfo: QuoteResquestLineWithBatchInfo[] = useMemo(
    () => groupSearchQuoteRequestLinePerBatch(quoteRequestLines),
    [quoteRequestLines],
  );

  const setQuoteRequestIsEditing = useSetAtom(quoteRequestisEditing);

  useEffect(() => {
    setQuoteRequestIsEditing(false);
  }, [setQuoteRequestIsEditing]);

  return (
    <PageLayout>
      <Group justify="center" align="flex-end" gap={"xl"}>
        <Can feature="searchSimilarPart">
          <SearchSimilarPart />
        </Can>

        <Can feature="predict">
          <PredictDropzone
            onSuccess={(quoteRequestId) => navigate(ROUTES.partnerQuoteRequest(quoteRequestId))}
          />
        </Can>
      </Group>

      {quoteRequestLinesCount === 0 && search.length === 0 ? (
        <NoQRLDisplay />
      ) : (
        <Stack mt="md" gap="xxs">
          <QuoteRequestLinesSearchBar search={search} setSearch={setSearch} />

          <QuoteRequestLinesList
            quoteRequestLinesWithBatchInfo={quoteRequestLinesWithBatchInfo}
            quoteRequestLinesCount={quoteRequestLinesCount}
            quoteRequestLinesItemsCountPerPage={quoteRequestLinesCountPerPage}
            isDataLoading={isLoading}
            sort={sort}
            setSort={setSort}
          />
        </Stack>
      )}
    </PageLayout>
  );
};
